import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext'; // import currency hook
import { HiMinus, HiPlus, HiChevronDown, HiShare, HiMail } from 'react-icons/hi';

const API =  process.env.REACT_APP_API_URL;

// Colour map for swatch backgrounds
const colorMap = {
  Black: '#000000',
  White: '#FFFFFF',
  Red: '#DC2626',
  Blue: '#2563EB',
  Green: '#16A34A',
  Grey: '#6B7280',
  Pink: '#EC4899',
  Beige: '#F5E6D3',
  Navy: '#1E3A8A',
  Brown: '#8B4513',
};

// –– Email Reminder Form Component ––
function EmailReminderForm({ productId }) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await axios.post(`${API}/api/products/${productId}/remind`, { email });
      setSubmitted(true);
    } catch (err) {
      alert('Something went wrong. Please try again.');
    }
  };

  if (submitted) {
    return <p className="text-sm text-green-600">We'll notify you when it drops!</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <input
        type="email"
        placeholder="Your email"
        className="flex-1 border border-gray-300 px-3 py-2 text-sm rounded focus:outline-none focus:border-black"
        value={email}
        onChange={e => setEmail(e.target.value)}
        required
      />
      <button
        type="submit"
        className="bg-black text-white px-4 py-2 text-sm font-medium rounded hover:bg-gray-800 transition"
      >
        Notify Me
      </button>
    </form>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { formatCurrency } = useCurrency(); // use context instead of local

  // Variant selection
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);

  // Image gallery
  const [activeImage, setActiveImage] = useState(0);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  // Countdown state for future drops
  const [timeLeft, setTimeLeft] = useState(null);

  // Modals
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [activeInfoTab, setActiveInfoTab] = useState(null);

  // Shipping info state
  const [shippingInfo, setShippingInfo] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
  });
  const [shippingErrors, setShippingErrors] = useState({});

  // Adding‑to‑cart state
  const [isAdding, setIsAdding] = useState(false);

  // Image helper
  const getImageUrl = (url) => {
    if (!url) return 'https://via.placeholder.com/600';
    if (url.startsWith('/uploads/')) {
      return `${API}/images/${url.replace('/uploads/', '')}`;
    }
    return url;
  };

  // Fetch product
  useEffect(() => {
    // axios.get(`${API}/api/products/${id}`)
    axios.get(`${API}/api/products/${id}?_=${Date.now()}`)
      .then(res => {
        setProduct(res.data);
        const firstVariant = res.data.variants?.[0];
        if (firstVariant) {
          setSelectedSize(firstVariant.size);
          setSelectedColor(firstVariant.color || '');
        }
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  // Countdown logic for future drops
  useEffect(() => {
    if (!product?.releaseDate) return;
    const release = new Date(product.releaseDate);
    if (release <= new Date()) return;

    const updateCountdown = () => {
      const now = new Date();
      const diff = release - now;
      if (diff <= 0) {
        setTimeLeft(null);
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeLeft({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [product]);

  const uniqueSizes = [...new Set(product?.variants?.map(v => v.size) || [])];
  const uniqueColors = [...new Set(product?.variants?.map(v => v.color).filter(Boolean) || [])];
  const showColorSelector = uniqueColors.length > 0;

  const soldOutSizes = new Set();
  if (product?.variants) {
    for (const size of uniqueSizes) {
      const hasStock = product.variants.some(v => v.size === size && v.stock > 0);
      if (!hasStock) soldOutSizes.add(size);
    }
  }

  const currentVariant = product?.variants?.find(v =>
    v.size === selectedSize &&
    (showColorSelector ? v.color === selectedColor : true)
  );

  const isFutureDrop = product?.releaseDate && new Date(product.releaseDate) > new Date();

  // Add to cart with loading and redirect
  const handleAddToCart = async () => {
    if (isFutureDrop) return;
    if (!selectedSize) {
      alert('Please select a size');
      return;
    }
    if (showColorSelector && !selectedColor) {
      alert('Please select a colour');
      return;
    }
    if (currentVariant?.stock === 0) {
      alert('This variant is sold out');
      return;
    }

    setIsAdding(true);
    const finalColor = showColorSelector ? selectedColor : '';

    await new Promise(resolve => setTimeout(resolve, 600));

    addToCart(product, {
      size: selectedSize,
      color: finalColor,
      price: product.basePrice,
      stock: currentVariant?.stock || 0,
    }, quantity);

    setIsAdding(false);
    navigate('/cart');
  };

  const handleShippingInputChange = (e) => {
    const { name, value } = e.target;
    setShippingInfo(prev => ({ ...prev, [name]: value }));
    if (shippingErrors[name]) {
      setShippingErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateShippingInfo = () => {
    const errors = {};
    if (!shippingInfo.fullName.trim()) errors.fullName = 'Full name is required';
    if (!shippingInfo.email.trim()) errors.email = 'Email is required';
    if (!shippingInfo.phone.trim()) errors.phone = 'Phone number is required';
    if (!shippingInfo.address.trim()) errors.address = 'Address is required';
    if (!shippingInfo.city.trim()) errors.city = 'City is required';
    if (!shippingInfo.state.trim()) errors.state = 'State is required';
    setShippingErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openCheckoutModal = () => {
    if (isFutureDrop) return;
    if (!selectedSize) {
      alert('Please select a size');
      return;
    }
    if (showColorSelector && !selectedColor) {
      alert('Please select a colour');
      return;
    }
    setShippingInfo({
      fullName: user?.displayName || '',
      email: user?.email || '',
      phone: '',
      address: '',
      city: '',
      state: '',
    });
    setShowCheckoutModal(true);
  };

  const processSquadPayment = async () => {
    if (!validateShippingInfo()) return;
    setShowCheckoutModal(false);
    try {
      const order = {
        items: [{
          productId: product.id,
          productName: product.name,
          price: currentVariant?.price || product.basePrice,
          quantity: quantity,
          size: selectedSize,
          color: showColorSelector ? selectedColor : '',
        }],
        total: (currentVariant?.price || product.basePrice) * quantity,
        status: 'PENDING',
        shippingAddress: shippingInfo,
      };
      const resOrder = await axios.post(`${API}/api/orders`, order);
      const orderId = resOrder.data.id;
      const initRes = await axios.post(`${API}/api/squad/initialize/${orderId}`);
      const { transaction_ref, checkout_url } = initRes.data?.data || {};
      if (checkout_url) {
        window.location.href = checkout_url;
      } else {
        alert('Payment initialization failed');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to initiate payment');
    }
  };

  if (loading) return <div className="text-center py-20">Loading...</div>;
  if (!product) return <div className="text-center py-20">Product not found</div>;

  const allImages = product.imageUrls?.length ? product.imageUrls : ['https://via.placeholder.com/600'];
  const price = product.basePrice;

  // Compute shipping threshold dynamically
  const freeShippingThresholdNGN = 200000;

  return (
    <div className="min-h-screen bg-white">
      {/* Promo banner */}
      <div className="bg-black text-white text-xs text-center py-2.5 px-4 font-light tracking-wide">
        Free shipping on orders over {formatCurrency(freeShippingThresholdNGN)} —{' '}
        <Link to="/products" className="underline underline-offset-2 hover:opacity-80">Shop now</Link>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 pt-8 pb-24">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-gray-400 mb-12 tracking-wider uppercase">
          <Link to="/" className="hover:text-black">Home</Link>
          <span className="text-gray-300">/</span>
          <Link to="/products" className="hover:text-black">Shop</Link>
          <span className="text-gray-300">/</span>
          <span className="text-black">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Image gallery – click to zoom lightbox */}
          <div className="space-y-4">
            <div className="relative aspect-[3/4] bg-gray-50 overflow-hidden group">
              <img
                src={getImageUrl(allImages[activeImage])}
                alt={product.name}
                className={`w-full h-full object-cover ${isFutureDrop ? 'blur-md' : ''}`}
              />
              {!isFutureDrop && (
                <button
                  onClick={() => setFullscreenOpen(true)}
                  className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow hover:bg-white transition"
                  aria-label="View fullscreen"
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </button>
              )}
              {isFutureDrop && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  {/* <span className="text-white text-lg font-bold uppercase tracking-widest bg-black/60 px-6 py-2 rounded">
                    Coming Soon
                  </span> */}
                </div>
              )}
            </div>

            {allImages.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(idx)}
                    className={`aspect-square bg-gray-50 overflow-hidden border-2 transition-colors ${activeImage === idx ? 'border-black' : 'border-transparent hover:border-gray-300'}`}
                  >
                    <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product details */}
          <div className="flex flex-col">
            <div className="sticky top-24 space-y-8">
              {isFutureDrop ? (
                <div className="space-y-4">
                  <h1 className="text-4xl lg:text-5xl font-black text-black leading-none">{product.name}</h1>
                  <p className="text-sm text-gray-600 leading-relaxed font-light">{product.description}</p>
                  {timeLeft && (
                    <div className="flex gap-2 text-center mt-6">
                      <div className="bg-gray-100 px-3 py-2 rounded">
                        <span className="text-xl font-bold">{timeLeft.days}</span>
                        <span className="text-xs block">Days</span>
                      </div>
                      <div className="bg-gray-100 px-3 py-2 rounded">
                        <span className="text-xl font-bold">{timeLeft.hours}</span>
                        <span className="text-xs block">Hours</span>
                      </div>
                      <div className="bg-gray-100 px-3 py-2 rounded">
                        <span className="text-xl font-bold">{timeLeft.minutes}</span>
                        <span className="text-xs block">Minutes</span>
                      </div>
                      <div className="bg-gray-100 px-3 py-2 rounded">
                        <span className="text-xl font-bold">{timeLeft.seconds}</span>
                        <span className="text-xs block">Seconds</span>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      const url = window.location.href;
                      if (navigator.share) {
                        navigator.share({ title: product.name, url });
                      } else {
                        navigator.clipboard.writeText(url).then(() => alert('Link copied!'));
                      }
                    }}
                    className="mt-6 flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-black transition"
                  >
                    <HiShare size={18} />
                    Share
                  </button>
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <HiMail size={16} /> Notify me when available
                    </p>
                    <EmailReminderForm productId={product.id} />
                  </div>
                  <p className="text-sm text-gray-500 mt-4">This item will be available for purchase after the countdown ends.</p>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    <h1 className="text-4xl lg:text-5xl font-black text-black leading-none">{product.name}</h1>
                    <p className="text-2xl font-light text-black">{formatCurrency(price)}</p>
                    <p className="text-sm text-gray-600 leading-relaxed font-light">{product.description}</p>
                  </div>

                  {/* Size selector */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-black uppercase tracking-wider">Size</span>
                      <button onClick={() => setShowSizeGuide(true)} className="text-xs text-gray-500 hover:text-black underline underline-offset-4">Size guide</button>
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                      {uniqueSizes.map(size => {
                        const isSoldOut = soldOutSizes.has(size);
                        return (
                          <button
                            key={size}
                            onClick={() => !isSoldOut && setSelectedSize(size)}
                            disabled={isSoldOut}
                            className={`py-3 text-sm font-medium transition-all ${
                              isSoldOut
                                ? 'bg-gray-100 text-gray-400 line-through cursor-not-allowed border border-gray-200'
                                : selectedSize === size
                                  ? 'bg-black text-white'
                                  : 'bg-white text-gray-700 border border-gray-200 hover:border-black'
                            }`}
                          >
                            {size}
                          </button>
                        );
                      })}
                    </div>
                    {!selectedSize && <p className="text-xs text-red-500">Please select a size</p>}
                  </div>

                  {showColorSelector && (
                    <div className="space-y-3">
                      <span className="text-xs font-medium text-black uppercase tracking-wider">Colour</span>
                      <div className="flex gap-3">
                        {uniqueColors.map(color => {
                          const bgColor = colorMap[color] || '#CCCCCC';
                          const isWhite = color.toLowerCase() === 'white';
                          return (
                            <button
                              key={color}
                              onClick={() => setSelectedColor(color)}
                              className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition ${selectedColor === color ? 'border-black' : 'border-gray-300'}`}
                              style={{ backgroundColor: bgColor }}
                              title={color}
                            >
                              {isWhite && <span className="w-6 h-6 rounded-full border border-gray-300" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    <span className="text-xs font-medium text-black uppercase tracking-wider">Quantity</span>
                    <div className="inline-flex border border-gray-200">
                      <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-12 h-12 flex items-center justify-center text-gray-600 hover:bg-gray-50" disabled={quantity <= 1}>
                        <HiMinus size={16} />
                      </button>
                      <span className="w-14 text-center text-sm font-light flex items-center justify-center border-x border-gray-200">{quantity}</span>
                      <button onClick={() => setQuantity(q => q + 1)} className="w-12 h-12 flex items-center justify-center text-gray-600 hover:bg-gray-50">
                        <HiPlus size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Add to Cart button */}
                  <button
                    onClick={handleAddToCart}
                    disabled={
                      isAdding ||
                      !selectedSize ||
                      (showColorSelector && !selectedColor) ||
                      (currentVariant?.stock === 0)
                    }
                    className="w-full py-4 text-sm font-medium tracking-wider uppercase bg-black text-white hover:bg-gray-900 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isAdding ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                        Adding to cart…
                      </>
                    ) : (
                      `Add to cart — ${formatCurrency(price * quantity)}`
                    )}
                  </button>
                </>
              )}

              <button
                onClick={() => {
                  const url = window.location.href;
                  if (navigator.share) {
                    navigator.share({ title: product.name, url });
                  } else {
                    navigator.clipboard.writeText(url).then(() => alert('Link copied!'));
                  }
                }}
                className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-black transition"
              >
                <HiShare size={18} />
                Share
              </button>

              <div className="border-t border-gray-200 pt-6 space-y-4">
                {[
                  { key: 'wash', title: 'Wash Care', content: 'Wash with mild liquid soap or bar soap. Avoid detergents – they can damage the print and fabric. Cold wash recommended. Do not bleach. Iron inside out on low heat.' },
                  { key: 'delivery', title: 'Delivery & Returns', content: `Free shipping over ${formatCurrency(freeShippingThresholdNGN)}. Delivery within 7-10 business days. Easy returns within 7 days of delivery.` },
                  { key: 'size', title: 'Size & Fit', content: 'Model is 5\'11" and wears size L. Regular fit. True to size.' },
                ].map(({ key, title, content }) => (
                  <div key={key} className="border-b border-gray-200">
                    <button
                      onClick={() => setActiveInfoTab(activeInfoTab === key ? null : key)}
                      className="w-full flex items-center justify-between py-4 text-left"
                    >
                      <span className="text-xs font-medium text-black uppercase tracking-wider">{title}</span>
                      <HiChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${activeInfoTab === key ? 'rotate-180' : ''}`} />
                    </button>
                    {activeInfoTab === key && (
                      <div className="pb-4 text-sm text-gray-600 font-light leading-relaxed">{content}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen lightbox */}
      {fullscreenOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setFullscreenOpen(false)}
        >
          <button
            onClick={() => setFullscreenOpen(false)}
            className="absolute top-5 right-5 text-white text-3xl hover:text-gray-300 transition"
            aria-label="Close fullscreen"
          >
            ✕
          </button>
          <img
            src={getImageUrl(allImages[activeImage])}
            alt={product.name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Shipping Modal */}
      <AnimatePresence>
        {showCheckoutModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => setShowCheckoutModal(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between">
                <h2 className="text-xl font-bold">Shipping Information</h2>
                <button onClick={() => setShowCheckoutModal(false)} className="text-gray-400 hover:text-black">✕</button>
              </div>
              <div className="p-5 space-y-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex gap-3">
                    <div className="w-16 h-20 bg-gray-100 rounded-lg overflow-hidden"><img src={getImageUrl(allImages[0])} alt="" className="w-full h-full object-cover" /></div>
                    <div>
                      <p className="font-medium">{product.name}</p>
                      <p className="text-sm text-gray-500">Size: {selectedSize} × {quantity}</p>
                      <p className="text-lg font-bold">{formatCurrency(price * quantity)}</p>
                    </div>
                  </div>
                </div>
                {['fullName','email','phone','address','city','state'].map(field => (
                  <div key={field}>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                      {field === 'fullName' ? 'Full Name *' : field.charAt(0).toUpperCase() + field.slice(1) + ' *'}
                    </label>
                    <input
                      type={field === 'email' ? 'email' : 'text'}
                      name={field}
                      value={shippingInfo[field]}
                      onChange={handleShippingInputChange}
                      className={`w-full px-4 py-3 bg-gray-50 border-2 rounded-xl text-black focus:outline-none ${shippingErrors[field] ? 'border-red-300' : 'border-gray-100 focus:border-black'}`}
                    />
                    {shippingErrors[field] && <p className="text-red-500 text-xs mt-1">{shippingErrors[field]}</p>}
                  </div>
                ))}
                <button onClick={processSquadPayment} className="w-full bg-black text-white py-4 rounded-xl font-semibold hover:bg-gray-900 transition">
                  Proceed to Payment
                </button>
                <p className="text-xs text-gray-400 text-center">You will be redirected to Squad secure payment gateway</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Size Guide Modal – includes 3XL */}
      {showSizeGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowSizeGuide(false)} />
          <div className="relative bg-white max-w-xl w-full p-8 rounded-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-xl font-light">Size Guide</h3>
              <button onClick={() => setShowSizeGuide(false)} className="text-gray-400 hover:text-black">✕</button>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left py-3 text-xs font-medium uppercase">Size</th>
                  <th className="text-left py-3 text-xs font-medium uppercase">Chest</th>
                  <th className="text-left py-3 text-xs font-medium uppercase">Length</th>
                  <th className="text-left py-3 text-xs font-medium uppercase">Shoulder</th>
                </tr>
              </thead>
              <tbody>
                {['S','M','L','XL','XXL','3XL'].map(size => {
                  let chest, length, shoulder;
                  switch (size) {
                    case 'S': chest = '38-40"'; length = '28"'; shoulder = '17"'; break;
                    case 'M': chest = '40-42"'; length = '29"'; shoulder = '18"'; break;
                    case 'L': chest = '42-44"'; length = '30"'; shoulder = '19"'; break;
                    case 'XL': chest = '44-46"'; length = '31"'; shoulder = '20"'; break;
                    case 'XXL': chest = '46-48"'; length = '32"'; shoulder = '21"'; break;
                    case '3XL': chest = '48-50"'; length = '33"'; shoulder = '22"'; break;
                    default: chest = '-'; length = '-'; shoulder = '-';
                  }
                  return (
                    <tr key={size} className="border-b border-gray-100">
                      <td className="py-4 text-sm font-medium">{size}</td>
                      <td className="py-4 text-sm text-gray-600">{chest}</td>
                      <td className="py-4 text-sm text-gray-600">{length}</td>
                      <td className="py-4 text-sm text-gray-600">{shoulder}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-xs text-gray-400 mt-6">Measurements are in inches. Allow 0.5-1" variation. Size up for a relaxed fit.</p>
          </div>
        </div>
      )}
    </div>
  );
}